"use client";

import {
  motion,
  useReducedMotion,
  AnimatePresence,
  type HTMLMotionProps,
} from "framer-motion";
import { forwardRef, type ReactNode, type CSSProperties } from "react";
import {
  fadeDown,
  fade,
  scaleUp,
  scalePop,
  staggerContainer,
  staggerOrchestrator,
  pageTransition,
  pageFade,
  drawerLeft,
  drawerRight,
  drawerBottom,
  modal,
  backdrop,
  accordionContent,
} from "./variants";
import { spring } from "./transitions";

type BaseProps = {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  delay?: number;
};

export function MotionSafe({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const shouldReduce = useReducedMotion();
  if (shouldReduce) return <>{fallback ?? children}</>;
  return <>{children}</>;
}

export const Fade = forwardRef<HTMLDivElement, BaseProps & { duration?: number }>(
  ({ children, className, style, delay = 0, duration = 0.45 }, ref) => (
    <motion.div
      ref={ref}
      className={className}
      style={style}
      variants={fade}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration, delay }}
    >
      {children}
    </motion.div>
  )
);
Fade.displayName = "Fade";

export const FadeUp = forwardRef<
  HTMLDivElement,
  BaseProps & { y?: number; as?: "div" | "section" | "article" | "li" | "p" }
>(({ children, className, style, delay = 0, y = 28, as = "div" }, ref) => {
  const Tag = motion[as] as typeof motion.div;
  return (
    <Tag
      ref={ref}
      className={className}
      style={style}
      initial={{ opacity: 0, y }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -14 }}
      transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </Tag>
  );
});
FadeUp.displayName = "FadeUp";

export const FadeDown = forwardRef<HTMLDivElement, BaseProps>(
  ({ children, className, style, delay = 0 }, ref) => (
    <motion.div
      ref={ref}
      className={className}
      style={style}
      variants={fadeDown}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ delay }}
    >
      {children}
    </motion.div>
  )
);
FadeDown.displayName = "FadeDown";

export const FadeLeft = forwardRef<HTMLDivElement, BaseProps & { x?: number }>(
  ({ children, className, style, delay = 0, x = 20 }, ref) => (
    <motion.div
      ref={ref}
      className={className}
      style={style}
      initial={{ opacity: 0, x: -x }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -12 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.div>
  )
);
FadeLeft.displayName = "FadeLeft";

export const FadeRight = forwardRef<HTMLDivElement, BaseProps & { x?: number }>(
  ({ children, className, style, delay = 0, x = 20 }, ref) => (
    <motion.div
      ref={ref}
      className={className}
      style={style}
      initial={{ opacity: 0, x }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 12 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.div>
  )
);
FadeRight.displayName = "FadeRight";

export const ScaleUp = forwardRef<HTMLDivElement, BaseProps>(
  ({ children, className, style, delay = 0 }, ref) => (
    <motion.div
      ref={ref}
      className={className}
      style={style}
      variants={scaleUp}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ delay }}
    >
      {children}
    </motion.div>
  )
);
ScaleUp.displayName = "ScaleUp";

export const ScalePop = forwardRef<HTMLDivElement, BaseProps>(
  ({ children, className, style, delay = 0 }, ref) => (
    <motion.div
      ref={ref}
      className={className}
      style={style}
      variants={scalePop}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ ...spring.bouncy, delay }}
    >
      {children}
    </motion.div>
  )
);
ScalePop.displayName = "ScalePop";

type StaggerProps = {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  stagger?: number;
  delayChildren?: number;
  transparent?: boolean;
};

export const StaggerList = forwardRef<HTMLDivElement, StaggerProps>(
  (
    {
      children,
      className,
      style,
      stagger = 0.08,
      delayChildren = 0,
      transparent = false,
    },
    ref
  ) => {
    const variantFn = transparent ? staggerOrchestrator : staggerContainer;
    return (
      <motion.div
        ref={ref}
        className={className}
        style={style}
        variants={variantFn(stagger, delayChildren)}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        {children}
      </motion.div>
    );
  }
);
StaggerList.displayName = "StaggerList";

export const StaggerUl = forwardRef<HTMLUListElement, StaggerProps>(
  ({ children, className, style, stagger = 0.08, delayChildren = 0, transparent = false }, ref) => {
    const variantFn = transparent ? staggerOrchestrator : staggerContainer;
    return (
      <motion.ul
        ref={ref}
        className={className}
        style={style}
        variants={variantFn(stagger, delayChildren)}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        {children}
      </motion.ul>
    );
  }
);
StaggerUl.displayName = "StaggerUl";

type InViewProps = BaseProps & {
  direction?: "up" | "down" | "left" | "right" | "scale";
  margin?: string;
  once?: boolean;
};

export const InView = forwardRef<HTMLDivElement, InViewProps>(
  (
    {
      children,
      className,
      style,
      delay = 0,
      direction = "up",
      margin = "-60px",
      once = true,
    },
    ref
  ) => {
    const variants = {
      up:    { initial: { opacity: 0, y: 36 }, animate: { opacity: 1, y: 0 } },
      down:  { initial: { opacity: 0, y: -24 }, animate: { opacity: 1, y: 0 } },
      left:  { initial: { opacity: 0, x: -28 }, animate: { opacity: 1, x: 0 } },
      right: { initial: { opacity: 0, x: 28 },  animate: { opacity: 1, x: 0 } },
      scale: { initial: { opacity: 0, scale: 0.92 }, animate: { opacity: 1, scale: 1 } },
    }[direction];

    return (
      <motion.div
        ref={ref}
        className={className}
        style={style}
        initial={variants.initial}
        whileInView={variants.animate}
        viewport={{ once, margin }}
        transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay }}
      >
        {children}
      </motion.div>
    );
  }
);
InView.displayName = "InView";

export const PageWrapper = forwardRef<
  HTMLDivElement,
  BaseProps & { mode?: "slide" | "fade" | "rise" }
>(({ children, className, style, mode = "rise" }, ref) => {
  const variants = mode === "fade" ? pageFade : pageTransition;
  return (
    <motion.div
      ref={ref}
      className={className}
      style={style}
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {children}
    </motion.div>
  );
});
PageWrapper.displayName = "PageWrapper";

type DrawerProps = BaseProps & {
  open: boolean;
  side?: "left" | "right" | "bottom";
};

export function Drawer({ children, className, style, open, side = "left" }: DrawerProps) {
  const variants = { left: drawerLeft, right: drawerRight, bottom: drawerBottom }[side];
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className={className}
          style={style}
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

type ModalWrapperProps = { children: ReactNode; open: boolean; className?: string };

export function ModalWrapper({ children, open, className }: ModalWrapperProps) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            variants={backdrop}
            initial="initial"
            animate="animate"
            exit="exit"
            className="fixed inset-0 bg-black/50 z-40"
          />
          <motion.div
            key="modal"
            variants={modal}
            initial="initial"
            animate="animate"
            exit="exit"
            className={className}
          >
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export function AccordionPanel({
  children,
  open,
  className,
}: {
  children: ReactNode;
  open: boolean;
  className?: string;
}) {
  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          key="content"
          variants={accordionContent}
          initial="collapsed"
          animate="expanded"
          exit="collapsed"
          className={className}
          style={{ overflow: "hidden" }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

type MotionButtonProps = HTMLMotionProps<"button"> & {
  lift?: boolean;
};

export const MotionButton = forwardRef<HTMLButtonElement, MotionButtonProps>(
  ({ children, lift = true, ...props }, ref) => (
    <motion.button
      ref={ref}
      whileHover={lift ? { y: -2, transition: spring.snappy } : undefined}
      whileTap={{ scale: 0.96, transition: spring.snappy }}
      {...props}
    >
      {children}
    </motion.button>
  )
);
MotionButton.displayName = "MotionButton";

export function StaggerText({
  text,
  className,
  delay = 0,
  stagger = 0.06,
  style,
}: {
  text: string;
  className?: string;
  delay?: number;
  stagger?: number;
  style?: CSSProperties;
}) {
  const words = text.split(" ");
  return (
    <motion.span
      className={className}
      style={{ display: "inline", ...style }}
      initial="initial"
      animate="animate"
      variants={{
        initial: {},
        animate: { transition: { staggerChildren: stagger, delayChildren: delay } },
      }}
    >
      {words.map((word, i) => (
        <motion.span
          key={i}
          style={{ display: "inline-block", whiteSpace: "pre" }}
          variants={{
            initial: { opacity: 0, y: "0.35em" },
            animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
          }}
        >
          {word}
          {i < words.length - 1 ? " " : ""}
        </motion.span>
      ))}
    </motion.span>
  );
}

export function PresenceSwitch({
  children,
  contentKey,
  className,
}: {
  children: ReactNode;
  contentKey: string | number;
  className?: string;
}) {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={contentKey}
        className={className}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}